import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import { cssProperties } from "../../../utils/commonCssProperties";
import commonActions from "../../../redux/common/actions";
import examScheduleActions from "../../../redux/examSchedule/actions";
import CancelButton from "../../../common/button/cancelButton";
import BluePrimaryButton from "../../../common/button/bluePrimaryButton";
import ButtonLoader from "../../../common/buttonLoader";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 640,
  bgcolor: `${cssProperties?.backgroundcolor?.secondary}`,
  padding: "24px",
  borderRadius: "8px",
  overflowY: "auto",
  maxHeight: "calc(100vh - 20px)",
  scrollbarWidth: "thin",
};

const defaultValues = {
  section_no: 1,
  section_title: "",
  row_no: 1,
  label: "",
  examdate: "",
  starttime: "",
  endtime: "",
  note: "",
  show_weekday: false,
  is_instruction: false,
};

const toMinutes = (time) => {
  if (!time) return null;
  const [hours, minutes] = time.split(":").map(Number);
  return hours * 60 + minutes;
};

/**
 * Add / edit one SECTION row — a label + value line inside a
 * "DETAILS OF CET EXAMINATION" block, ordered by (section_no, row_no).
 */
export default function SectionRowModal({ courseid }) {
  const dispatch = useDispatch();
  const { showModal, buttonLoader } = useSelector((state) => state.commonReducer);
  const { backofficeTokenDetails } = useSelector((state) => state.authReducer);

  const open = showModal?.show && showModal?.method === "ESM";
  const row = showModal?.data;

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    trigger,
    formState: { errors },
  } = useForm({ defaultValues });

  // examdate and starttime satisfy the same "a row needs one of these" rule, so
  // filling either one has to clear the error sitting on the other
  const revalidateDateOrTime = () => trigger(["examdate", "starttime", "endtime"]);

  useEffect(() => {
    if (!open) return;
    reset(
      row
        ? {
            section_no: row?.section_no ?? 1,
            section_title: row?.section_title ?? "",
            row_no: row?.row_no ?? 1,
            label: row?.label ?? "",
            examdate: row?.examdate ?? "",
            starttime: row?.starttime ?? "",
            endtime: row?.endtime ?? "",
            note: row?.note ?? "",
            show_weekday: !!row?.show_weekday,
            is_instruction: !!row?.is_instruction,
          }
        : defaultValues
    );
  }, [open, row, reset]);

  const isInstruction = !!watch("is_instruction");

  const handleClose = () => {
    dispatch({
      type: commonActions.SET_SHOW_MODAL,
      payload: { show: false, type: null, method: null, data: null },
    });
  };

  const onSubmit = (form) => {
    // an instruction row prints its label alone, so it is stored without the
    // date/time it may have carried before the box was ticked
    const instruction = !!form?.is_instruction;

    // the whole row goes on every write — the API writes omitted fields as NULL
    const data = {
      courseid: Number(courseid),
      row_type: "SECTION",
      section_no: Number(form?.section_no),
      section_title: form?.section_title?.trim() || null,
      row_no: Number(form?.row_no),
      label: form?.label?.trim(),
      examdate: instruction ? null : form?.examdate || null,
      starttime: instruction ? null : form?.starttime || null,
      endtime: instruction ? null : form?.endtime || null,
      note: instruction ? null : form?.note?.trim() || null,
      show_weekday: !instruction && !!form?.show_weekday,
      is_instruction: instruction,
      userid: Number(backofficeTokenDetails?.id),
    };

    dispatch(
      row
        ? {
            type: examScheduleActions.UPDATE_SCHEDULE_ROW,
            payload: { id: row?.id, data, handleClose },
          }
        : {
            type: examScheduleActions.CREATE_SCHEDULE_ROW,
            payload: { data, handleClose },
          }
    );
  };

  return (
    <Modal open={!!open} aria-labelledby="section-row-modal">
      <Box sx={style}>
        <Typography
          variant="h5"
          sx={{
            color: `${cssProperties?.color?.primary}`,
            fontWeight: "500",
            marginBottom: "18px",
          }}
        >
          {row ? "Edit Schedule Row" : "Add Schedule Row"}
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Section No"
              InputLabelProps={{ shrink: true }}
              {...register("section_no", {
                required: "Section no is required",
                min: { value: 1, message: "Must be 1 or more" },
              })}
              error={!!errors?.section_no}
              helperText={errors?.section_no?.message}
              FormHelperTextProps={{ style: { margin: 0 } }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label="Row No"
              InputLabelProps={{ shrink: true }}
              {...register("row_no", {
                required: "Row no is required",
                min: { value: 1, message: "Must be 1 or more" },
              })}
              error={!!errors?.row_no}
              helperText={errors?.row_no?.message}
              FormHelperTextProps={{ style: { margin: 0 } }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Section Title"
              placeholder="1.Name of the Examination- PART A & B: ..."
              InputLabelProps={{ shrink: true }}
              {...register("section_title")}
              helperText="Printed as the block heading. Only the first row of a section needs it."
              FormHelperTextProps={{ style: { margin: 0 } }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label={
                <span>
                  Label
                  <span className="error" style={{ color: "red" }}> *</span>
                </span>
              }
              placeholder="Entry in examination hall"
              InputLabelProps={{ shrink: true }}
              {...register("label", { required: "Label is required" })}
              error={!!errors?.label}
              helperText={errors?.label?.message}
              FormHelperTextProps={{ style: { margin: 0 } }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Exam Date"
              InputLabelProps={{ shrink: true }}
              {...register("examdate", {
                onChange: revalidateDateOrTime,
                // every branch has to coerce to a real boolean — a validate that
                // returns a string is read as the error message, so returning the
                // field's own value would fail the form with the date as its error.
                // getValues, not the render closure, so the checkbox revalidates
                // in the same tick it is toggled
                validate: (value) =>
                  !!getValues("is_instruction") ||
                  !!value ||
                  !!getValues("starttime") ||
                  "A row needs either a date or a start time",
              })}
              error={!!errors?.examdate}
              helperText={errors?.examdate?.message}
              FormHelperTextProps={{ style: { margin: 0 } }}
              disabled={isInstruction}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              type="time"
              label="Start Time"
              InputLabelProps={{ shrink: true }}
              {...register("starttime", {
                onChange: revalidateDateOrTime,
                validate: (value) =>
                  !!getValues("is_instruction") ||
                  !!value ||
                  !!getValues("examdate") ||
                  "A row needs either a date or a start time",
              })}
              error={!!errors?.starttime}
              helperText={errors?.starttime?.message}
              FormHelperTextProps={{ style: { margin: 0 } }}
              disabled={isInstruction}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              type="time"
              label="End Time"
              InputLabelProps={{ shrink: true }}
              {...register("endtime", {
                validate: (value) => {
                  if (!value) return true;
                  const start = getValues("starttime");
                  if (!start) return "End time cannot be set without a start time";
                  return (
                    toMinutes(value) > toMinutes(start) ||
                    "End time must be after start time"
                  );
                },
              })}
              error={!!errors?.endtime}
              helperText={errors?.endtime?.message}
              FormHelperTextProps={{ style: { margin: 0 } }}
              disabled={isInstruction}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Note"
              placeholder="Long bell"
              InputLabelProps={{ shrink: true }}
              {...register("note")}
              helperText="Appended to the printed value — 10.45 AM - Long bell"
              FormHelperTextProps={{ style: { margin: 0 } }}
              disabled={isInstruction}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={!!watch("show_weekday")}
                  onChange={(e) => setValue("show_weekday", e.target.checked)}
                  disabled={isInstruction}
                />
              }
              label="Show weekday"
              sx={{ "& .MuiFormControlLabel-label": { fontSize: "14px" } }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={isInstruction}
                  onChange={(e) => {
                    setValue("is_instruction", e.target.checked);
                    revalidateDateOrTime();
                  }}
                />
              }
              label="Instruction row"
              sx={{ "& .MuiFormControlLabel-label": { fontSize: "14px" } }}
            />
            <Typography sx={{ fontSize: "12px", color: "text.secondary" }}>
              An instruction row prints full width — only its label, no date or time.
            </Typography>
          </Grid>
        </Grid>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "12px", marginTop: "32px" }}>
          <CancelButton handleBack={handleClose} />
          <BluePrimaryButton
            title={buttonLoader ? <ButtonLoader /> : "Submit"}
            handleSubmit={handleSubmit(onSubmit)}
          />
        </Box>
      </Box>
    </Modal>
  );
}
